import facebook from '../../../../assets/icons/ic_facebook.svg';
import twitter from '../../../../assets/icons/ic_twitter.svg';
import telegram from '../../../../assets/icons/ic_telegram.svg';
import whatsapp from '../../../../assets/icons/ic_whatsapp.svg';
import logo from '../../../../assets/images/logo_white_letter.svg';
import {ReactComponent as ShareOutIcon} from "../../../../assets/icons/ic_share_out.svg";
import ChannelIcon from '../../common/ChannelIcon';
import { useDictionary } from '../../../../utilities/hooks/UseDictionary';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { QRCode } from 'react-qrcode-logo';
import { FacebookShareButton, TelegramShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';

const ShareExperienceDialog = ({ websiteEnabled, websiteUrl, offlineEnabled, offlineUrl, setShareDialogVisibility }) => {
	const [opacity, setOpacity] = useState(0);
	const [isCopied, setIsCopied] = useState(false);

	const dictionary = useDictionary();

	const channels = useMemo(() => {
		const list = [];
		if (websiteEnabled) list.push({ id: 'website', channelId: 2, label: dictionary.website, url: websiteUrl ?? '' });
		if (offlineEnabled) list.push({ id: 'offline', channelId: 4, label: dictionary.offline, url: offlineUrl ?? '' });
		return list;
	}, [websiteEnabled, websiteUrl, offlineEnabled, offlineUrl, dictionary]);

	const [selectedChannelId, setSelectedChannelId] = useState(channels[0]?.id);
	const selectedChannel = channels.find((c) => c.id === selectedChannelId) ?? channels[0];
	const shareUrl = selectedChannel?.url ?? '';

	const onCopyClicked = () => {
		navigator.clipboard.writeText(shareUrl).then(
			function () {
				setIsCopied(true);
				setTimeout(() => {
					setIsCopied(false);
				}, 2000);
			},
			// eslint-disable-next-line no-unused-vars
			function (err) {}
		);
	};

	const onCancelClicked = () => {
		setOpacity(0);
		setTimeout(() => {
			setShareDialogVisibility(false);
		}, 800);
	};

	const downloadQRCode = () => {
		// Generate download with use canvas and stream
		const canvas = document.getElementById('qr-gen');
		const pngUrl = canvas
			.toDataURL('image/png')
			.replace('image/png', 'image/octet-stream');
		let downloadLink = document.createElement('a');
		downloadLink.href = pngUrl;
		downloadLink.download = `${shareUrl}.png`;
		document.body.appendChild(downloadLink);
		downloadLink.click();
		document.body.removeChild(downloadLink);
	};

	useEffect(() => {
		setTimeout(() => {
			setOpacity(1);
		}, 50);
	}, []);

	return (
		<div className="dialog-wrapper" style={{ opacity: opacity }}>
			<div className="share-dialog-container" data-testid="share-dialog">
				<div className="share-dialog-side-image" />
				<div className="share-dialog-content-container">
					<div className={`share-dialog-header${channels.length > 1 ? ' share-dialog-header--no-border' : ''}`}>
						<div className="login-logo" style={{ marginTop: '0' }} />
						<div
							onClick={onCancelClicked}
							className="dialog-close-button"
							style={{ position: 'unset' }}
						/>
					</div>
					{channels.length > 1 ? (
						<div
							className="share-channel-tabs"
							data-testid="share-channel-toggle"
						>
							{channels.map((channel) => (
								<div
									key={channel.id}
									onClick={() => setSelectedChannelId(channel.id)}
									className={`settings-tab${selectedChannelId === channel.id ? ' settings-tab-active' : ''}`}
									data-testid={`share-channel-toggle-${channel.id}`}
								>
									<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
										<ChannelIcon channelId={channel.channelId} />
										{channel.id === 'website' ? dictionary.website_link : dictionary.offline_link}
									</span>
								</div>
							))}
						</div>
					) : null}
					<div className="flex-filler" />
					<h3
						style={{ fontStyle: 'normal', marginBottom: 16 }}
						className="share-dialog-title"
						data-testid="share-dialog-title"
					>
						{dictionary.share_on_socials}
					</h3>
					<div className="share-dialog-icons-container">
						<WhatsappShareButton
							url={shareUrl}
							quote={'I just created an experience on Upiria!'}
							hashtag="#upiria"
						>
							<div
								className="share-dialog-icon"
								style={{ backgroundImage: 'url(' + whatsapp + ')' }}
							/>
						</WhatsappShareButton>
						<FacebookShareButton
							url={shareUrl}
							quote={'I just created an experience on Upiria!'}
							hashtag="#upiria"
						>
							{/* <FacebookIcon size={32} round /> */}
							<div
								className="share-dialog-icon"
								style={{ backgroundImage: 'url(' + facebook + ')' }}
							/>
						</FacebookShareButton>
						<TwitterShareButton
							url={shareUrl}
							quote={'I just created an experience on Upiria!'}
							hashtag="#upiria"
						>
							<div
								className="share-dialog-icon"
								style={{ backgroundImage: 'url(' + twitter + ')' }}
							/>
						</TwitterShareButton>
						<TelegramShareButton
							url={shareUrl}
							quote={'I just created an experience on Upiria!'}
							hashtag="#upiria"
						>
							<div
								className="share-dialog-icon"
								style={{ backgroundImage: 'url(' + telegram + ')' }}
							/>
						</TelegramShareButton>
					</div>
					<div className="flex-filler" />
					<h3 style={{ fontStyle: 'normal', marginTop: 40, marginBottom: 16 }}>
						{dictionary.copy_link}
					</h3>
					<div className="share-link-header">
						<div
							className="share-link-text-container"
							data-testid="share-link-text-container"
						>
							<div className="share-link-text">{shareUrl}</div>
							<div
								className={`share-link-copy-icon ${isCopied ? 'copied' : ''}`}
								onClick={onCopyClicked}
								data-testid="share-link-copy-icon"
							></div>
						</div>
						<a href={shareUrl} className="secondary-button open-new-button" target="_blank" rel="noreferrer">
							<ShareOutIcon />
						</a>
					</div>
					<div className="flex-filler" />
					<h3 style={{ fontStyle: 'normal', marginTop: 40, marginBottom: 16 }}>
						{dictionary.copy_qr_code}
					</h3>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<QRCode
							value={shareUrl}
							id="qr-gen"
							logoPadding={4}
							logoImage={logo}
							logoHeight={28}
							logoWidth={28}
							removeQrCodeBehindLogo={true}
							eyeColor={[
								{ inner: '#e55a5c', outer: 'black' },
								{ inner: '#e55a5c', outer: 'black' },
								{ inner: '#e55a5c', outer: 'black' }
							]}
						/>
						<div
							className="secondary-button"
							style={{ marginLeft: 10, fontFamily: 'Roboto' }}
							onClick={downloadQRCode}
						>
							{dictionary.download_code}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

ShareExperienceDialog.propTypes = {
	websiteEnabled: PropTypes.bool,
	websiteUrl: PropTypes.string,
	offlineEnabled: PropTypes.bool,
	offlineUrl: PropTypes.string,
	setShareDialogVisibility: PropTypes.func
};

export default ShareExperienceDialog;
